export class PhoneNumber {
    /**
     * Country calling codes mapping
     * Organized by length for efficient parsing
     */
    private static readonly COUNTRY_CODES = {
        // 1-digit codes
        one: new Set(['1', '7']),

        // 3-digit codes (must check before 2-digit to avoid false matches)
        three: new Set([
            '358', '370', '371', '372', '373', '374', '375', '376', '377', '378',
            '380', '381', '382', '383', '385', '386', '387', '389', '420', '421', '423'
        ]),

        // 2-digit codes (most common - ranges 20-99 excluding 1-digit codes)
        // We validate by checking if it's in the valid range
        isValidTwoDigit: (code: string): boolean => {
            const num: number = parseInt(code, 10);
            return num >= 20 && num <= 99;
        }
    };

    public readonly nationalNumber: string;
    public readonly countryCode: string;

    private constructor(nationalNumber: string, countryCode: string) {
        this.nationalNumber = nationalNumber;
        this.countryCode = countryCode;
    }

    /**
     * Parse an international phone number in E.164 format (e.g., "+61412345678")
     * and extract the national number (without country code)
     *
     * @param phoneNumber - Phone number string starting with "+"
     * @returns PhoneNumber instance with nationalNumber and countryCode, or null if invalid
     */
    public static parse(phoneNumber: string): PhoneNumber | null {
        if (!phoneNumber || typeof phoneNumber !== 'string') {
            return null;
        }

        // Remove all whitespace and formatting characters (spaces, hyphens, parentheses, dots)
        const cleaned: string = phoneNumber.replace(/[\s\-\(\)\.]/g, '');

        // Must start with + for international format
        if (!cleaned.startsWith('+')) {
            return null;
        }

        // Remove the leading + and validate remaining characters are digits
        const digits: string = cleaned.substring(1);

        if (!/^\d+$/.test(digits) || digits.length < 4) {
            return null; // Invalid: must be all digits and have at least country code + some number
        }

        // Try to extract country code by checking lengths in order: 3, 2, 1
        // This matches the approach used by libphonenumber-js

        // Check 3-digit country codes first (to avoid false matches with 2-digit)
        if (digits.length >= 4) {
            const threeDigit: string = digits.substring(0, 3);
            if (PhoneNumber.COUNTRY_CODES.three.has(threeDigit)) {
                return new PhoneNumber(digits.substring(3), threeDigit);
            }
        }

        // Check 2-digit country codes
        if (digits.length >= 3) {
            const twoDigit: string = digits.substring(0, 2);
            if (PhoneNumber.COUNTRY_CODES.isValidTwoDigit(twoDigit)) {
                return new PhoneNumber(digits.substring(2), twoDigit);
            }
        }

        // Check 1-digit country codes (USA/Canada = 1, Russia/Kazakhstan = 7)
        if (digits.length >= 2) {
            const oneDigit: string = digits.substring(0, 1);
            if (PhoneNumber.COUNTRY_CODES.one.has(oneDigit)) {
                return new PhoneNumber(digits.substring(1), oneDigit);
            }
        }

        // If no valid country code found, return null
        return null;
    }
}
